import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { sdk } from '@/services/sdk';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/kit';
import { FormDialog, PickSelect, SwitchRow } from '@/components/inputs';
import { ANNOUNCEMENT_TONES, SHOW_ON } from '@/shared/constants';
import { committed, fromLocalInput, toLocalInput } from '@/shared/data';

export const SHOW_ON_LABELS = { Site: 'Storefront', Account: 'Customer account', Both: 'Storefront and account' };

export const LIVE_STYLES = {
  Live: 'border-success-border bg-success-muted text-success',
  Ended: 'border-warning-border bg-warning-muted text-warning',
  Off: 'border-border bg-muted text-muted-foreground',
};

export function liveState(a, now = Date.now()) {
  if (!a?.active_c) return 'Off';
  if (a.ends_at_c && new Date(a.ends_at_c).getTime() < now) return 'Ended';
  return 'Live';
}

function fromAnnouncement(a) {
  return {
    Name: a?.Name ?? '',
    tone_c: a?.tone_c || 'Info',
    show_on_c: a?.show_on_c || 'Both',
    active_c: a ? !!a.active_c : true,
    ends_at_c: toLocalInput(a?.ends_at_c),
  };
}

/** Post or edit a banner shown across the top of the storefront and/or the account. */
export default function AnnouncementDialog({ open, onOpenChange, announcement, onSaved }) {
  const editing = !!announcement;
  const [form, setForm] = useState(fromAnnouncement(announcement));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(fromAnnouncement(announcement));
  }, [open]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const text = (key) => (e) => set(key)(e.target.value);

  async function submit() {
    if (!form.Name.trim()) {
      toast.error('Write the announcement');
      return;
    }
    const fields = {
      Name: form.Name.trim(),
      tone_c: form.tone_c,
      show_on_c: form.show_on_c,
      active_c: !!form.active_c,
      ends_at_c: fromLocalInput(form.ends_at_c),
    };
    setSaving(true);
    try {
      const res = editing
        ? await sdk.table('announcements_c').update({ Id: announcement.Id, ...fields })
        : await sdk.table('announcements_c').create(fields);
      const saved = committed(res, editing ? 'Announcement updated' : 'Announcement posted');
      if (!saved) return;
      onOpenChange(false);
      setForm(fromAnnouncement(null));
      onSaved?.(saved);
    } finally {
      setSaving(false);
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? 'Edit announcement' : 'New announcement'}
      description={editing ? undefined : 'A one-line banner. Outage banners show in ember, promos in wraith.'}
      onSubmit={submit}
      saving={saving}
      submitLabel={editing ? 'Save changes' : 'Post announcement'}
      busyLabel={editing ? 'Saving…' : 'Posting…'}
    >
      <Field label="Message" htmlFor="an-name">
        <Input id="an-name" value={form.Name} onChange={text('Name')} placeholder="Servers upgrade Tuesday 03:00 UTC. Expect a short drop." />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Tone" htmlFor="an-tone">
          <PickSelect id="an-tone" value={form.tone_c} onChange={set('tone_c')} values={ANNOUNCEMENT_TONES} placeholder="Info, Promo or Outage" />
        </Field>
        <Field label="Shows on" htmlFor="an-show">
          <PickSelect id="an-show" value={form.show_on_c} onChange={set('show_on_c')} values={SHOW_ON} labels={SHOW_ON_LABELS} placeholder="Where it shows" />
        </Field>
      </div>
      <Field label="Ends" htmlFor="an-ends" hint="It comes down by itself after this. Empty to keep it up.">
        <Input id="an-ends" type="datetime-local" value={form.ends_at_c} onChange={text('ends_at_c')} placeholder="2026-11-04T04:00" />
      </Field>
      <SwitchRow id="an-active" label="Live" hint="Switch off to take it down now." checked={form.active_c} onChange={set('active_c')} />
    </FormDialog>
  );
}
